import { useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublications } from '../hooks';
import { PublicationTableView } from '../components/PublicationTableView';
import { PublicationCalendarView } from '../components/PublicationCalendarView';
import { PublicationKanbanView } from '../components/PublicationKanbanView';
import { PublicationDetailModal } from '../components/PublicationDetailModal';
import type { Publication } from '../types';

type DatePreset = 'all' | 'this-month' | 'next-month' | 'last-month' | 'upcoming' | 'past' | 'no-date' | 'custom';

interface DateFilter {
  preset: DatePreset;
  from: string; // yyyy-mm-dd
  to: string;   // yyyy-mm-dd
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function resolvePreset(preset: DatePreset, current: DateFilter): { from: string; to: string } {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  switch (preset) {
    case 'this-month':  return { from: fmt(startOfMonth(now)), to: fmt(endOfMonth(now)) };
    case 'next-month': {
      const n = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { from: fmt(startOfMonth(n)), to: fmt(endOfMonth(n)) };
    }
    case 'last-month': {
      const l = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: fmt(startOfMonth(l)), to: fmt(endOfMonth(l)) };
    }
    case 'upcoming':   return { from: todayISO(), to: '' };
    case 'past':       return { from: '', to: todayISO() };
    case 'no-date':    return { from: '', to: '' };
    case 'custom':     return { from: current.from, to: current.to };
    case 'all':
    default:           return { from: '', to: '' };
  }
}

function isPublicationInRange(p: Publication, filter: DateFilter): boolean {
  if (filter.preset === 'all') return true;
  if (filter.preset === 'no-date') return !p.publishDate;
  if (!p.publishDate) return false;
  const d = p.publishDate.slice(0, 10);
  if (filter.from && d < filter.from) return false;
  if (filter.to && d > filter.to) return false;
  return true;
}

const PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'upcoming',   label: 'Upcoming' },
  { value: 'this-month', label: 'This month' },
  { value: 'next-month', label: 'Next month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'past',       label: 'Past' },
  { value: 'no-date',    label: 'No date' },
  { value: 'custom',     label: 'Custom' },
];

export function PublicationsPage() {
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = usePublications(search ? { search } : {});
  const [selected, setSelected] = useState<Publication | null>(null);

  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: 'all', from: '', to: '' });

  const applyPreset = (preset: DatePreset) => {
    const { from, to } = resolvePreset(preset, dateFilter);
    setDateFilter({ preset, from, to });
  };

  const setCustomFrom = (from: string) => setDateFilter((s) => ({ ...s, preset: 'custom', from }));
  const setCustomTo = (to: string) => setDateFilter((s) => ({ ...s, preset: 'custom', to }));

  const allPublications = data?.publications ?? [];

  const publications = useMemo(
    () => allPublications.filter((p) => isPublicationInRange(p, dateFilter)),
    [allPublications, dateFilter],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Publication</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${publications.length} of ${allPublications.length} publications` : '—'}
          </p>
        </div>
        <Input
          placeholder="Search title…"
          className="max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded p-3 text-sm">
          Failed to load publications: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {/* Date filter bar */}
      <div className="rounded-lg border bg-white p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">
            Publish date
          </span>
          {PRESETS.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={dateFilter.preset === p.value ? 'default' : 'outline'}
              onClick={() => applyPreset(p.value)}
              className="h-7 px-3 text-xs"
            >
              {p.label}
            </Button>
          ))}
          {(dateFilter.preset !== 'all' && dateFilter.preset !== 'no-date') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => applyPreset('all')}
              className="h-7 px-2 text-xs text-slate-500"
            >
              Clear
            </Button>
          )}
        </div>

        {(dateFilter.preset === 'custom' ||
          (dateFilter.preset !== 'all' &&
            dateFilter.preset !== 'no-date' &&
            (dateFilter.from || dateFilter.to))) && (
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="date-from" className="text-slate-500">From</Label>
              <Input
                id="date-from"
                type="date"
                className="h-7 text-xs w-36"
                value={dateFilter.from}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Label htmlFor="date-to" className="text-slate-500">To</Label>
              <Input
                id="date-to"
                type="date"
                className="h-7 text-xs w-36"
                value={dateFilter.to}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
          <TabsTrigger value="table">Table</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          {isLoading ? (
            <Skeleton className="h-[600px] w-full" />
          ) : (
            <PublicationCalendarView publications={publications} onEventClick={setSelected} />
          )}
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          {isLoading ? (
            <Skeleton className="h-[600px] w-full" />
          ) : (
            <PublicationKanbanView publications={publications} onCardClick={setSelected} />
          )}
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          {isLoading ? (
            <Skeleton className="h-[600px] w-full" />
          ) : (
            <PublicationTableView publications={publications} onRowClick={setSelected} />
          )}
        </TabsContent>
      </Tabs>

      <PublicationDetailModal
        publication={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
